import { createContext, useState, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { api } from 'api'
import * as AuthActions from '../authed/auth.actions'
import { showSnackbar } from '../global/snackbar/snackbar.actions'
import { selectFile } from '../global/utils/fileUtil'
import { t } from '@lingui/core/macro'

export const userContext = createContext({})

export const UserProvider = ({ children, onClose }) => {
  const userTypes = {
    account_owner: t`Administrator`,
    manager: t`Manager`,
    standard_user: t`User`,
  }

  const [byId, setById] = useState({})
  const dispatch = useDispatch()

  const initializeUsers = data => {
    const newData = data ? Object.values(data) : []
    setById(() =>
      newData.reduce(
        (acc, row) => ({
          ...acc,
          [row.id]: { ...row, type: userTypes[row.role] },
        }),
        {},
      ),
    )
    return newData
  }

  const fetchUsers = useMemo(
    () => () => api.get('/api/v1/account/user/list').then(({ data }) => initializeUsers(data)),
    [],
  )

  const fetchUser = useMemo(
    () => id =>
      api.get(`/api/v1/account/user/get/${id}`).then(({ data: { user } }) => {
        setById(prev => ({ ...prev, [user.id]: { ...user, type: userTypes[user.role] } }))
        return user
      }),
    [],
  )

  const editUser = useMemo(
    () => user =>
      api.post(`/api/v1/account/user/store-or-update`, user).then(({ data }) => {
        const newData = data.message ? data.user : data
        setById(prev => ({ ...prev, [newData.id]: { ...newData, type: userTypes[newData.role] } }))
      }),
    [],
  )

  const selectAndImportPhoto = type =>
    selectFile('image/png, image/jpeg, image/gif').then(file => {
      const formData = new FormData()
      formData.append(type, file)
      return formData
    })

  const updateUserProps = userProps => {
    setById({
      ...byId,
      [userProps.id]: {
        ...byId[userProps.id],
        ...userProps,
      },
    })
  }

  const updateAvatar = useMemo(
    () => formData =>
      api.post(`/api/v1/user/avatar`, formData).then(response => updateUserProps(response.data)),
  )

  const updateUserFields = useMemo(() => profileFields => {
    api
      .patch('/api/v1/user/profile', profileFields)
      .then(() => {
        dispatch(
          showSnackbar({
            message: 'User was saved successfully',
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
        updateUserProps(profileFields)
        return profileFields
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: 'User updates failed to save. Please try again',
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  })

  const updateAlertEmails = async alertEmails =>
    api.put('/api/v1/account/alertEmails', { alertEmails })

  const getAlertEmails = async () => api.get('/api/v1/account/alertEmails', {})

  const setLogo = formData => {
    api
      .post('/api/v1/account/logo', formData)
      .then(({ data }) => {
        dispatch(AuthActions.updateCompanyInfo(data))
        dispatch(
          showSnackbar({
            message: 'Logo was updated successfully',
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: 'New logo failed to save. Please try again',
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
    onClose()
  }

  const updateOrgName = useCallback(name =>
    api
      .put('/api/v1/account/name', {
        name,
      })
      .then(resp => {
        dispatch(AuthActions.updateCompanyInfo(resp.data))
        return resp
      }),
  )

  const updateSettings = settings => {
    const { allow_approval } = settings
    return api
      .put('/api/v1/account/settings', {
        allow_approval,
      })
      .then(res => {
        dispatch(AuthActions.updateCompanyInfo(res.data))
        return res
      })
  }

  const value = useMemo(
    () => ({
      byId,
      fetchUsers,
      initializeUsers,
      fetchUser,
      editUser,
      selectAndImportPhoto,
      updateUserProps,
      updateAvatar,
      updateUserFields,
      setLogo,
      updateOrgName,
      updateAlertEmails,
      getAlertEmails,
      updateSettings,
    }),
    [byId, fetchUsers, fetchUser, editUser, initializeUsers],
  )

  return <userContext.Provider value={value}>{children}</userContext.Provider>
}
